import { useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { SOCKET_URL } from '../config'
import { registerSocket, unregisterSocket } from '../lib/socketRegistry'

export type SocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected'

class AdminEventDeduplicator {
  private buffer: string[] = []
  private lastEventId: string | null = null

  async getLastEventId(): Promise<string | null> {
    if (this.lastEventId) return this.lastEventId
    const val = sessionStorage.getItem('rapidry_last_event_id_admin')
    if (val) this.lastEventId = val
    return val
  }

  processEvent(eventId: string | undefined): boolean {
    if (!eventId) return true
    if (this.buffer.includes(eventId)) return false
    
    this.buffer.push(eventId)
    if (this.buffer.length > 100) this.buffer.shift()
    
    this.lastEventId = eventId
    sessionStorage.setItem('rapidry_last_event_id_admin', eventId)
    return true
  }
}
const adminDeduplicator = new AdminEventDeduplicator()

type SocketHandlers = Record<string, (data: any) => void>

/**
 * Lightweight client-side schema check (Req 1.4 / Phase E task 1).
 * Mirrors the backend validator's required fields. We only WARN on
 * mismatch - handlers still run so a schema-drifted event doesn't break
 * the UI. The backend is the source of truth.
 */
const EXPECTED_FIELDS: Record<string, string[]> = {
  'order:created': ['order_id', 'order_number', 'status', 'updated_at'],
  'order:status_changed': ['order_id', 'order_number', 'status', 'updated_at'],
  'order:agent_assigned': ['order_id', 'order_number', 'agent_id', 'updated_at'],
  'order:agent_unassigned': ['order_id', 'order_number', 'agent_id', 'updated_at'],
  'order:payment_updated': ['order_id', 'order_number', 'payment_status', 'updated_at'],
  'order:cash_collected': ['order_id', 'order_number', 'amount', 'agent_id', 'collected_at'],
  'order:cancelled': ['order_id', 'order_number', 'status', 'updated_at'],
  'order:refunded': ['order_id', 'order_number', 'status', 'updated_at'],
  'agent:location_updated': ['agent_id', 'lat', 'lng', 'recorded_at'],
  'agent:online_changed': ['agentId', 'is_online', 'occurred_at'],
  'order:assigned': ['order_id', 'order_number', 'status', 'updated_at'],
  'agent:status_changed': ['agent_id', 'is_online'],
  'notification:new': ['notification_id', 'user_id', 'type', 'title'],
  'agent:kyc_status_changed': ['agent_id', 'verification_status'],
  'agent:kyc_doc_uploaded': ['agent_id', 'doc_type', 'uploaded_at'],
  'agent:kyc_doc_reviewed': ['agent_id', 'doc_id', 'doc_type', 'status', 'changed_at'],
  'order:reschedule_requested': ['order_id', 'type', 'new_date', 'new_slot', 'requested_at'],
}

const validatePayload = (event: string, data: any): void => {
  const required = EXPECTED_FIELDS[event]
  if (!required || !data || typeof data !== 'object') return
  const missing = required.filter((key) => data[key] === undefined || data[key] === null)
  if (missing.length > 0) {
    console.warn(
      `[useSocket] ${event} payload missing fields: ${missing.join(', ')}`,
      data
    )
  }
}

/**
 * Connects once to the admin Socket.IO room and dispatches incoming events to
 * caller-supplied handlers. Returns the socket plus a live connection `status`
 * pages can surface ("Live" vs "Reconnecting…").
 *
 * Implementation notes:
 *  - Callers pass an inline `events` object each render. We keep it in a ref so
 *    handler bodies always see the latest captured state, and we only (re)bind
 *    listeners when the *set of event names* changes - not on every render - so
 *    an inline prop never churns off/on.
 *  - Connection lifecycle (connect/disconnect/connect_error/reconnect) drives
 *    `status`; on every (re)connect we re-emit socket:catch_up so missed events
 *    are recovered.
 */
export function useSocket(events: SocketHandlers): { socket: Socket | null; status: SocketStatus } {
  // Socket is held in state so the returned value is reactive (no ref reads
  // during render).
  const [socket, setSocket] = useState<Socket | null>(null)
  const [status, setStatus] = useState<SocketStatus>('connecting')

  // Always-latest handlers without re-establishing the connection. Updated in an
  // effect (not during render) so handler bodies see the latest captured state.
  const eventsRef = useRef(events)
  useEffect(() => {
    eventsRef.current = events
  })

  // Stable signature of the event-name set so the bind effect only re-runs when
  // the set changes, not when inline handler identities change each render.
  const eventKeys = Object.keys(events).sort().join(',')

  useEffect(() => {
    const s = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem('rapidry_admin_token'),
      },
      // Reconnect with a sane backoff so the dashboard recovers from transient
      // WebSocket drops without a manual refresh.
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.3,
    })

    const sendCatchUp = async () => {
      const lastId = await adminDeduplicator.getLastEventId()
      if (lastId) {
        s.emit('socket:catch_up', { room: 'admin', lastEventId: lastId })
      }
    }

    s.on('connect', () => {
      setStatus('connected')
      void sendCatchUp()
    })
    s.on('disconnect', (reason) => {
      // 'io client disconnect' = we tore it down (unmount/logout); anything else
      // is a drop that socket.io will auto-retry.
      setStatus(reason === 'io client disconnect' ? 'disconnected' : 'reconnecting')
    })
    s.on('connect_error', () => {
      setStatus('reconnecting')
    })
    // Manager-level reconnection lifecycle (socket.io v4).
    s.io.on('reconnect_attempt', () => setStatus('reconnecting'))
    s.io.on('reconnect', () => {
      setStatus('connected')
      void sendCatchUp()
    })
    s.io.on('error', () => setStatus('reconnecting'))

    registerSocket(s)
    setSocket(s)

    return () => {
      unregisterSocket(s)
      s.disconnect()
      setSocket(null)
    }
  }, [])

  useEffect(() => {
    if (!socket) return

    const keys = eventKeys ? eventKeys.split(',') : []

    keys.forEach((event) => {
      socket.off(event)
      socket.on(event, (data: any) => {
        const eventId = data?.event_id
        if (!adminDeduplicator.processEvent(eventId)) return

        validatePayload(event, data)
        eventsRef.current[event]?.(data)
      })
    })

    return () => {
      keys.forEach((event) => {
        socket.off(event)
      })
    }
  }, [socket, eventKeys])

  return { socket, status }
}
